/**
 * request.js - HTTP requests, content swapping, and View Transitions
 *
 * Handles:
 * - URL inference from href/action/formaction
 * - Method inference from method/formmethod
 * - Fetch with proper headers
 * - Content swapping with multiple strategies
 * - View Transitions API integration
 */

import { resolve, hasAttr } from './cascade.js';
import { updateHistory } from './navigation.js';
import { getFromCache, setInCache, parseDuration, parseCacheConfig, invalidateCacheByPattern } from './cache.js';
import { swapWithTransition, hasViewTransitions } from './transitions.js';

// Registry for custom swap strategies (e.g., morphing)
const swapStrategies = new Map();
let requestCounter = 0;

/**
 * Register a custom swap strategy
 *
 * @param {string} name
 * @param {(target: HTMLElement, html: string, context?: Object) => void} handler
 */
export function registerSwapStrategy(name, handler) {
  if (!name || typeof handler !== 'function') return;
  swapStrategies.set(name, handler);
}

/**
 * Unregister a custom swap strategy
 *
 * @param {string} name
 */
export function unregisterSwapStrategy(name) {
  swapStrategies.delete(name);
}

/**
 * Default morph strategy (optional)
 * Uses global Idiomorph if present; falls back to innerHTML.
 *
 * @param {HTMLElement} target
 * @param {string} html
 */
function defaultMorphSwap(target, html) {
  const lib = typeof window !== 'undefined'
    ? (window.Idiomorph || window.idiomorph)
    : null;

  if (lib) {
    if (typeof lib.morph === 'function') {
      lib.morph(target, html);
      return;
    }
    if (typeof lib === 'function') {
      lib(target, html);
      return;
    }
  }

  console.warn('htmlstar: morph strategy requested but no morphing library found. Falling back to inner swap.');
  target.innerHTML = html;
}

// Register default morph strategy (opt-in via data-swap="morph")
registerSwapStrategy('morph', defaultMorphSwap);

// Track aria state so we can restore pre-existing values
const ariaState = new WeakMap();

function setAria(el, attr, value) {
  if (!el || !attr) return;
  let state = ariaState.get(el);
  if (!state) {
    state = {};
    ariaState.set(el, state);
  }

  if (value) {
    if (!(attr in state)) {
      state[attr] = el.getAttribute(attr);
    }
    el.setAttribute(attr, 'true');
  } else if (attr in state) {
    const prev = state[attr];
    if (prev === null) {
      el.removeAttribute(attr);
    } else {
      el.setAttribute(attr, prev);
    }
    delete state[attr];
  }

  if (Object.keys(state).length === 0) {
    ariaState.delete(el);
  }
}

function dispatchRequestEvent(el, name, detail) {
  if (!el) return;
  const event = new CustomEvent(name, {
    detail,
    bubbles: true,
    composed: true
  });
  el.dispatchEvent(event);
}

/**
 * Get URL from an element
 * Priority: data-href > href > action > formAction
 *
 * @param {HTMLElement} el
 * @returns {string|null}
 */
export function getURL(el) {
  return el.dataset.href || el.href || el.action || el.formAction || null;
}

/**
 * Get HTTP method from an element
 * Priority: data-method > method > formMethod > GET
 *
 * @param {HTMLElement} el
 * @returns {string}
 */
export function getMethod(el) {
  return (el.dataset.method || el.method || el.formMethod || 'GET').toUpperCase();
}

/**
 * Check if we should skip processing this element
 *
 * Skip conditions:
 * - Explicit opt-out (data-target="")
 * - No URL
 * - External URL (different origin)
 * - Anchor link (same page hash)
 * - Download attribute
 * - target="_blank"
 *
 * @param {HTMLElement} el
 * @param {string} url
 * @returns {boolean}
 */
export function shouldSkip(el, url) {
  // Explicit opt-out
  if (resolve(el, 'target') === '') return true;

  // No URL
  if (!url) return true;

  // URL checks
  try {
    const u = new URL(url, location.href);
    // External URL
    if (u.origin !== location.origin) return true;
    // Anchor link on same page
    if (u.hash && u.pathname === location.pathname && u.search === location.search) return true;
  } catch {
    return true;
  }

  // Non-GET without a form or explicit include
  const method = getMethod(el);
  if (method !== 'GET') {
    const hasForm = !!el.closest('form');
    const hasInclude = !!el.dataset.include;
    if (!hasForm && !hasInclude) return true;
  }

  // Download attribute
  if (el.hasAttribute('download')) return true;

  // Opens in new window
  if (el.target === '_blank') return true;

  return false;
}

/**
 * Swap content into a target element
 *
 * Strategies:
 * - inner: Replace innerHTML (default)
 * - outer: Replace entire element
 * - prepend: Insert at beginning
 * - append: Insert at end
 * - before: Insert before element
 * - after: Insert after element
 * - delete: Remove target
 * - none: Do nothing
 *
 * @param {HTMLElement} target - Target element
 * @param {string} html - HTML content to swap
 * @param {string} strategy - Swap strategy
 * @returns {Promise}
 */
export function swap(target, html, strategy = 'inner') {
  const customSwap = swapStrategies.get(strategy);
  const doSwap = () => {
    if (customSwap) {
      customSwap(target, html, { strategy });
      return;
    }
    switch (strategy) {
      case 'outer':
        target.outerHTML = html;
        break;
      case 'prepend':
        target.insertAdjacentHTML('afterbegin', html);
        break;
      case 'append':
      case 'beforeend':
        target.insertAdjacentHTML('beforeend', html);
        break;
      case 'before':
      case 'beforebegin':
        target.insertAdjacentHTML('beforebegin', html);
        break;
      case 'after':
      case 'afterend':
        target.insertAdjacentHTML('afterend', html);
        break;
      case 'delete':
        target.remove();
        break;
      case 'none':
        break;
      default:
        target.innerHTML = html;
    }
  };

  // Check if transitions are disabled
  const transitionsDisabled = resolve(target, 'transitions') === 'false';

  if (transitionsDisabled) {
    doSwap();
    return Promise.resolve();
  }

  // Use swapWithTransition which handles View Transitions API with CSS fallback
  return swapWithTransition(target, doSwap, {
    skipFallback: strategy === 'outer' || strategy === 'delete' // Skip fallback for element replacement
  });
}

/**
 * Extract a fragment from HTML using a selector
 *
 * @param {string} html - Full HTML response
 * @param {string} selector - CSS selector for fragment
 * @returns {string} - Extracted innerHTML or original if not found
 */
export function extractFragment(html, selector) {
  if (!selector) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fragments = doc.querySelectorAll(selector);

  if (fragments.length > 0) {
    // Return innerHTML to avoid nesting when target matches selector
    return Array.from(fragments).map(f => f.innerHTML).join('');
  }

  return html;
}

/**
 * Process out-of-band swaps
 * Elements with id attribute in response are swapped into matching elements
 *
 * @param {string} html - HTML response
 * @returns {string} - HTML with OOB elements removed
 */
export function processOOB(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const oobElements = doc.querySelectorAll('[data-oob]');

  for (const el of oobElements) {
    const id = el.id;
    if (id) {
      const target = document.getElementById(id);
      if (target) {
        const strategy = el.dataset.swap || 'outer';
        swap(target, el.outerHTML, strategy);
      }
    }
    el.remove();
  }

  return doc.body.innerHTML;
}

/**
 * Handle a request for an element
 *
 * @param {HTMLElement} el - Triggering element
 * @param {Event} e - Original event (will be prevented)
 * @param {Function} onInit - Callback to reinitialize new elements
 * @returns {Promise}
 */
export async function handleRequest(el, e, onInit) {
  const url = getURL(el);
  if (shouldSkip(el, url)) return;

  e?.preventDefault();

  // Clear prior error state
  delete el.dataset.error;
  delete el.dataset.errorMessage;

  // Check for confirmation
  const confirmMsg = resolve(el, 'confirm');
  if (confirmMsg && !confirm(confirmMsg)) return;

  // Resolve target (may be overridden by server via X-HTMLStar-Retarget)
  let targetSelector = resolve(el, 'target');
  if (!targetSelector) return;

  let target = targetSelector === 'self'
    ? el
    : document.querySelector(targetSelector);

  if (!target) {
    console.warn(`htmlstar: target "${targetSelector}" not found`);
    return;
  }

  // Clear target error state on new request
  delete target.dataset.error;

  // Resolve error boundary
  const errorTargetSelector = resolve(el, 'errorTarget');
  const errorTargetEl = errorTargetSelector ? document.querySelector(errorTargetSelector) : null;

  // Resolve loading indicators
  const loadingTargetSelector = resolve(el, 'loadingTarget');
  const loadingTargetEl = loadingTargetSelector ? document.querySelector(loadingTargetSelector) : null;
  const loadingClass = resolve(el, 'loadingClass');

  // Queue control
  const queueMode = resolve(el, 'queue') || 'last';
  if (queueMode === 'first' && el._htmlstarInFlight) {
    return; // Drop request — one is already in-flight
  }

  const method = getMethod(el);
  const swapStrategy = resolve(el, 'swap') || 'inner';
  const selectSelector = resolve(el, 'select');
  const loadTarget = target; // save reference before potential retarget

  const requestId = ++requestCounter;
  const requestDetail = {
    id: requestId,
    url,
    method,
    target: targetSelector,
    swap: swapStrategy,
    select: selectSelector,
    element: el
  };

  // Set loading state
  el.dataset.loading = '';
  el._htmlstarInFlight = true;
  if (hasAttr(el, 'disable')) {
    el.disabled = true;
  }
  setAria(el, 'aria-busy', true);
  if (hasAttr(el, 'disable')) {
    setAria(el, 'aria-disabled', true);
  }
  setAria(target, 'aria-busy', true);

  // Show loading indicator
  if (loadingTargetEl) loadingTargetEl.hidden = false;
  if (loadingClass) {
    el.classList.add(loadingClass);
    loadTarget.classList.add(loadingClass);
  }

  // Hide error boundary on new request
  if (errorTargetEl) errorTargetEl.hidden = true;

  try {
    dispatchRequestEvent(el, 'htmlstar:request-start', requestDetail);

    // Build request options
    const opts = {
      method,
      headers: {
        'X-Requested-With': 'htmlstar'
      }
    };

    // Parse custom headers
    const customHeaders = el.dataset.headers;
    if (customHeaders) {
      try {
        Object.assign(opts.headers, JSON.parse(customHeaders));
      } catch (e) {
        console.warn('htmlstar: Invalid headers JSON', e);
      }
    }

    // Send target/select headers when opt-in enabled (for server-side fragment detection)
    if (hasAttr(el, 'sendHeaders')) {
      opts.headers['X-HTMLStar-Target'] = targetSelector;
      if (selectSelector) {
        opts.headers['X-HTMLStar-Select'] = selectSelector;
      }
    }

    // Include form data for non-GET requests
    if (method !== 'GET') {
      const form = el.closest('form');
      if (form) {
        opts.body = new FormData(form);
      }
    }

    // Include additional inputs
    const includeSelector = el.dataset.include;
    if (includeSelector) {
      const formData = opts.body || new FormData();
      const inputs = includeSelector === 'self'
        ? [el]
        : document.querySelectorAll(includeSelector);

      for (const input of inputs) {
        if (input.name) {
          formData.append(input.name, input.value);
        }
      }

      if (method === 'GET') {
        // Append to URL as query params
        const params = new URLSearchParams(formData);
        const separator = url.includes('?') ? '&' : '?';
        opts.url = url + separator + params.toString();
      } else {
        opts.body = formData;
      }
    }

    // Make request
    const finalURL = opts.url || url;

    // Abort any in-flight request for this target (skip for 'all' mode)
    if (queueMode !== 'all' && target._htmlstarAbort) {
      target._htmlstarAbort.abort();
    }
    const controller = new AbortController();
    target._htmlstarAbort = controller;
    opts.signal = controller.signal;

    // Resolve cache configuration
    const cacheConfigStr = resolve(el, 'cache');
    const prefetchConfig = resolve(el, 'prefetch');
    const { ttl: cacheTTL, swr } = cacheConfigStr !== null
      ? parseCacheConfig(cacheConfigStr)
      : { ttl: 0, swr: false };
    const prefetchEnabled = prefetchConfig !== null && prefetchConfig !== 'false';
    const effectiveTTL = cacheTTL > 0
      ? cacheTTL
      : (prefetchEnabled ? parseDuration('true') : 0);
    const cacheStorage = resolve(el, 'cacheStorage') || 'memory';
    let html;
    let servedFromCache = false;

    // Check cache for GET requests
    if (method === 'GET' && effectiveTTL > 0) {
      const cached = getFromCache(finalURL, method, {
        allowStale: swr,
        storage: cacheStorage
      });
      if (cached) {
        html = cached.html;
        servedFromCache = true;

        // SWR: if stale, swap immediately then revalidate in background
        if (swr && cached.isStale) {
          // We have stale content — continue to fetch below
          // but the swap will happen with stale content first
        } else if (!cached.isStale) {
          // Fresh cache hit — skip fetch entirely
        }
      }
    }

    // Fetch if not cached (or SWR revalidation needed)
    let res;
    const needsFetch = !html || (swr && servedFromCache);

    if (!servedFromCache || (swr && servedFromCache)) {
      // For SWR with stale content, we already have html to swap.
      // We still fetch to revalidate, but only if we didn't get a fresh hit.
      if (servedFromCache && !swr) {
        // Fresh cache hit, skip fetch
      } else {
        const fetchAndProcess = async () => {
          const r = await fetch(finalURL, opts);

          if (!r.ok) {
            throw new Error(r.statusText);
          }

          // Check for server-side response headers
          const redirectURL = r.headers.get('X-HTMLStar-Redirect');
          if (redirectURL) {
            window.location.href = redirectURL;
            return { redirect: true };
          }

          const refresh = r.headers.get('X-HTMLStar-Refresh');
          if (refresh === 'true' || refresh === '') {
            window.location.reload();
            return { redirect: true };
          }

          // Check for retarget header
          const retargetSelector = r.headers.get('X-HTMLStar-Retarget');
          if (retargetSelector) {
            const newTarget = document.querySelector(retargetSelector);
            if (newTarget) {
              target = newTarget;
              targetSelector = retargetSelector;
            } else {
              console.warn(`htmlstar: retarget "${retargetSelector}" not found, using original target`);
            }
          }

          const text = await r.text();

          // Cache GET responses
          if (method === 'GET' && effectiveTTL > 0) {
            setInCache(finalURL, method, text, effectiveTTL, cacheStorage);
          }

          return { html: text, res: r };
        };

        if (swr && servedFromCache) {
          // SWR: revalidate in background after initial swap
          // We'll kick this off after the swap below
          const revalidate = fetchAndProcess().then(result => {
            if (result?.redirect) return;
            if (result?.html && result.html !== html) {
              // Content changed — re-swap
              let freshHtml = result.html;
              if (hasAttr(el, 'oob')) freshHtml = processOOB(freshHtml);
              if (selectSelector) freshHtml = extractFragment(freshHtml, selectSelector);
              swap(target, freshHtml, swapStrategy).then(() => {
                if (onInit && target.isConnected) onInit(target);
              });
              dispatchRequestEvent(el, 'htmlstar:request-end', {
                ...requestDetail,
                url: finalURL,
                status: result.res?.status,
                ok: result.res?.ok,
                fromCache: false
              });
            }
          }).catch(() => { /* SWR revalidation is best-effort */ });
          // Store promise for testability but don't await it
          el._htmlstarSWR = revalidate;
        } else {
          // Normal fetch
          const result = await fetchAndProcess();
          if (result?.redirect) return;
          html = result.html;
          res = result.res;
        }
      }
    }

    // Invalidate cache entries on non-GET success
    if (method !== 'GET') {
      const invalidatePatterns = resolve(el, 'cacheInvalidate');
      if (invalidatePatterns) {
        const patterns = invalidatePatterns.split(',').map(p => p.trim());
        for (const pattern of patterns) {
          if (pattern) invalidateCacheByPattern(pattern);
        }
      }
    }

    // Process out-of-band swaps
    if (hasAttr(el, 'oob')) {
      html = processOOB(html);
    }

    // Extract fragment if specified (uses cascade)
    if (selectSelector) {
      html = extractFragment(html, selectSelector);
    }

    // Save parent for outer swap (target will be detached)
    const initTarget = swapStrategy === 'outer' ? target.parentElement : target;

    // Perform swap
    target.dataset.swapping = '';
    await swap(target, html, swapStrategy);
    if (swapStrategy !== 'outer') {
      delete target.dataset.swapping;
    }

    // Auto-open dialog
    if (target.tagName === 'DIALOG' && !target.open) {
      target.showModal();
    }

    // Update history with state for back/forward navigation
    updateHistory(el, finalURL, {
      target: targetSelector,
      swap: swapStrategy,
      select: selectSelector
    });

    // Success state (skip for outer swap as target is detached)
    if (swapStrategy !== 'outer') {
      target.dataset.success = '';
      setTimeout(() => delete target.dataset.success, 2000);
    }

    // Reinitialize new content
    if (onInit && initTarget) {
      onInit(initTarget);
    }

    // Dispatch navigation event for UI sync (e.g., updating active nav state)
    window.dispatchEvent(new CustomEvent('htmlstar:navigated', {
      detail: {
        url: finalURL,
        target: targetSelector,
        swap: swapStrategy
      }
    }));

    dispatchRequestEvent(el, 'htmlstar:request-end', {
      ...requestDetail,
      url: finalURL,
      status: res?.status,
      ok: res?.ok,
      fromCache: !!html && !res
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      dispatchRequestEvent(el, 'htmlstar:request-abort', requestDetail);
      return;
    }
    el.dataset.error = '';
    el.dataset.errorMessage = err.message;
    target.dataset.error = '';
    console.error('htmlstar:', err);

    // Show error boundary
    if (errorTargetEl) {
      errorTargetEl.hidden = false;
      errorTargetEl._htmlstarRetry = () => handleRequest(el, null, onInit);
      // Wire up retry buttons via event delegation (once per error target)
      if (!errorTargetEl._htmlstarRetryDelegate) {
        errorTargetEl.addEventListener('click', (evt) => {
          if (evt.target.closest('[data-retry]') && errorTargetEl._htmlstarRetry) {
            errorTargetEl.hidden = true;
            errorTargetEl._htmlstarRetry();
          }
        });
        errorTargetEl._htmlstarRetryDelegate = true;
      }
    }

    dispatchRequestEvent(el, 'htmlstar:request-error', {
      ...requestDetail,
      error: err
    });
  } finally {
    delete el.dataset.loading;
    el._htmlstarInFlight = false;
    if (hasAttr(el, 'disable')) {
      el.disabled = false;
    }
    setAria(el, 'aria-busy', false);
    setAria(el, 'aria-disabled', false);
    setAria(target, 'aria-busy', false);

    // Hide loading indicator
    if (loadingTargetEl) loadingTargetEl.hidden = true;
    if (loadingClass) {
      el.classList.remove(loadingClass);
      loadTarget.classList.remove(loadingClass);
    }

    if (target._htmlstarAbort?.signal?.aborted || !target.isConnected) {
      delete target._htmlstarAbort;
    }
  }
}
